import { Injectable, DestroyRef, inject, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { filter, fromEvent, interval, merge, Subscription } from 'rxjs';

import { MajorEventTypes } from '../models/major-event.types';
import { isCurrentLocationPublic, isPublicAppRoute } from '../utils/public-route.util';
import { AUTH_STORAGE_TOKEN_KEY, AuthService } from './auth.service';
import { NotificationService } from './notification.service';
import { WorkflowEventsService } from './workflow/workflow-events.service';

/** How often to silently refresh an expiring access token while the user is idle. */
const TOKEN_REFRESH_CHECK_INTERVAL_MS = 15_000;

/**
 * Forces logout when the account is suspended/disabled/deactivated.
 * - SignalR MajorEvent `UserAccountStatusChanged` for online users
 * - Failed access-token refresh (inactive/disabled accounts rejected by refresh-token API)
 *
 * No periodic GET /api/Auth/session-status polling — refresh + SignalR cover force-logout.
 */
@Injectable({ providedIn: 'root' })
export class SessionLifecycleService {
  private readonly auth = inject(AuthService);
  private readonly workflowEvents = inject(WorkflowEventsService);
  private readonly notifications = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private forceLogoutHandled = false;
  private tokenRefreshSub?: Subscription;
  private crossTabLogoutHandled = false;

  constructor() {
    this.workflowEvents.events$
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (event.eventType !== MajorEventTypes.UserAccountStatusChanged) return;
        this.forceLogout({
          title: 'Account updated',
          message:
            event.description?.trim() ||
            'Your account status changed. Please sign in again.'
        });
      });

    effect(() => {
      const authenticated = this.auth.isAuthenticated();
      if (authenticated) {
        this.forceLogoutHandled = false;
        this.crossTabLogoutHandled = false;
        this.startTokenRefreshPolling();
        this.refreshAccessTokenIfNeeded();
      } else {
        this.stopTokenRefreshPolling();
      }
    });

    merge(
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible')
      ),
      fromEvent(window, 'focus')
    )
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.syncSessionFromOtherTabs();
        if (this.auth.isAuthenticated()) {
          this.refreshAccessTokenIfNeeded();
        }
      });

    if (typeof window !== 'undefined') {
      fromEvent<StorageEvent>(window, 'storage')
        .pipe(takeUntilDestroyed())
        .subscribe((event) => this.onCrossTabStorage(event));
    }
  }

  /** Another tab signed in/out — localStorage is shared; reconcile this tab's memory. */
  private onCrossTabStorage(event: StorageEvent): void {
    if (event.storageArea !== localStorage) return;
    if (event.key !== AUTH_STORAGE_TOKEN_KEY && event.key !== null) return;
    this.syncSessionFromOtherTabs();
  }

  private syncSessionFromOtherTabs(): void {
    const wasAuthenticated = this.auth.isAuthenticated();
    this.auth.syncSessionFromStorage();

    if (wasAuthenticated && !this.auth.isAuthenticated()) {
      this.handleCrossTabLogout();
    }
  }

  private handleCrossTabLogout(): void {
    if (this.crossTabLogoutHandled) return;
    this.crossTabLogoutHandled = true;
    this.stopTokenRefreshPolling();
    this.workflowEvents.stop();

    const url = this.router.url ?? '';
    if (isPublicAppRoute(url) || isCurrentLocationPublic()) {
      return;
    }
    if (!url.includes('/auth/login')) {
      void this.router.navigateByUrl('/auth/login');
    }
  }

  private startTokenRefreshPolling(): void {
    this.stopTokenRefreshPolling();
    this.tokenRefreshSub = interval(TOKEN_REFRESH_CHECK_INTERVAL_MS)
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.refreshAccessTokenIfNeeded());
  }

  private stopTokenRefreshPolling(): void {
    this.tokenRefreshSub?.unsubscribe();
    this.tokenRefreshSub = undefined;
  }

  /** Silently renew the access token before API calls fail (keeps idle tabs signed in). */
  private refreshAccessTokenIfNeeded(): void {
    if (!this.auth.isAuthenticated() || this.forceLogoutHandled) return;
    if (!this.auth.isAccessTokenExpired()) return;

    this.auth.ensureValidAccessToken().subscribe((valid) => {
      if (!valid) this.auth.handleExpiredSession();
    });
  }

  private forceLogout(toast: { title: string; message: string }): void {
    if (this.forceLogoutHandled || !this.auth.isAuthenticated()) return;
    this.forceLogoutHandled = true;
    this.stopTokenRefreshPolling();

    this.notifications.push({
      type: 'warning',
      title: toast.title,
      message: toast.message
    });

    this.workflowEvents.stop();
    this.auth.logout();

    const url = this.router.url ?? '';
    if (isPublicAppRoute(url) || isCurrentLocationPublic()) {
      return;
    }
    if (!url.includes('/auth/login')) {
      void this.router.navigateByUrl('/auth/login');
    }
  }
}
